import styled from '@emotion/styled';
import React, { useMemo, useState } from 'react';
import { mediaQueries } from 'src/shared/styles/variables';
import { CareNavigationSteps } from '../components/CareNavigationSteps';
import { useLocation, Redirect } from 'react-router';
import { CareNavigationRequest } from '../models/careNavigation';
import { useTranslation } from 'react-i18next';
import {
    BodyTextMedium,
    Button,
    ButtonGroup,
    Checkbox,
    HeadingText50,
    pxToRem,
} from '@modernhealth/vital-components';
import { navigationService } from 'src/config/rootService';
import { useTopicSelectionGroupsQuery } from 'src/modules/user/documents/topicSelectionGroupsQuery.generated';
import first from 'lodash/first';
import { isEmpty } from 'lodash';
import { useIsCareNavEnabledUser } from '../hooks/useIsCareNavEnabledUser';
import { ButtonGroupTestID } from '../models/testsIds';
import { filterNullish } from '@modernhealth/filter-nullish';

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',

    maxWidth: pxToRem(500),
    marginTop: pxToRem(100),
    marginRight: 'auto',
    marginLeft: 'auto',
    [mediaQueries.sm]: {
        padding: '0.5rem',
    },
});

const ButtonGroupContainer = styled.div({
    marginBottom: pxToRem(40),
});

const CheckboxGroup = styled.div({
    marginTop: pxToRem(24),
    marginBottom: pxToRem(8),
});

const CheckboxContainer = styled.div({
    marginBottom: pxToRem(24),
});

export const CareNavigationTopicsStep: React.FC = () => {
    const location = useLocation<CareNavigationRequest | undefined>();
    const { isUserEnabledFlow } = useIsCareNavEnabledUser();

    const { t } = useTranslation();
    const { data } = useTopicSelectionGroupsQuery({});
    const [subTopicsOfFocusCheckboxes, setSubTopicsOfFocusCheckboxes] =
        useState<Record<string, boolean | undefined>>();

    const requestState = useMemo<CareNavigationRequest | undefined>(() => {
        const selectedSubTopics = subTopicsOfFocusCheckboxes
            ? filterNullish(
                  Object.entries(subTopicsOfFocusCheckboxes).map(([key, value]) =>
                      // Selected subtopics should have 'true' for their value
                      value ? key : null,
                  ),
              )
            : undefined;

        if (!location.state) return undefined;

        return {
            ...location.state,
            subTopics: selectedSubTopics ?? location.state.subTopics,
        };
    }, [location.state, subTopicsOfFocusCheckboxes]);

    const subTopicsOfFocus = useMemo(() => {
        const topicSelectionGroup = first(data?.topicSelectionGroups);

        return (
            topicSelectionGroup?.subtopicSelections.map(item => {
                return {
                    uuid: item.subtopicOfFocus.uuid,
                    name: item.subtopicOfFocus.name,
                };
            }) ?? []
        );
    }, [data?.topicSelectionGroups]);

    const isContinueButtonEnabled = () => {
        return requestState?.subTopics && requestState.subTopics?.length > 0;
    };

    const handleCheckBoxChange = (key: string, checked: boolean) => {
        setSubTopicsOfFocusCheckboxes({
            ...subTopicsOfFocusCheckboxes,
            [key]: !checked,
        });
    };

    if (!isUserEnabledFlow) {
        return <Redirect to="/dashboard" />;
    }

    if (!location.state || isEmpty(location.state) || !requestState) {
        return <Redirect to="/care-navigation-request" />;
    }

    return (
        <Wrapper>
            <CareNavigationSteps step={2} flow={requestState.flow} />
            <HeadingText50 marginTop={32}>
                {t('careNavigation:care_navigation_topics_step.title')}
            </HeadingText50>
            <BodyTextMedium>
                {t('careNavigation:care_navigation_topics_step.select_title')}
            </BodyTextMedium>
            <CheckboxGroup>
                {subTopicsOfFocus.map(value => {
                    const checked = !!subTopicsOfFocusCheckboxes?.[value.uuid];

                    return (
                        <CheckboxContainer key={value.uuid}>
                            <Checkbox
                                value={checked}
                                label={value.name}
                                onChange={() => handleCheckBoxChange(value.uuid, checked)}
                            />
                        </CheckboxContainer>
                    );
                })}
            </CheckboxGroup>
            <ButtonGroupContainer>
                <ButtonGroup>
                    <Button
                        variant="secondary"
                        onClick={() =>
                            navigationService.navigate('/care-navigation-request', requestState)
                        }
                        data-testid={ButtonGroupTestID.FirstButton}
                    >
                        {t('common.back')}
                    </Button>
                    <Button
                        variant="primary"
                        size="large"
                        onClick={() =>
                            navigationService.navigate('/care-navigation-goals', requestState)
                        }
                        disabled={!isContinueButtonEnabled()}
                        data-testid={ButtonGroupTestID.SecondButton}
                    >
                        {t('common.continue')}
                    </Button>
                </ButtonGroup>
            </ButtonGroupContainer>
        </Wrapper>
    );
};
